import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, User, Edit, Download, Mail, FileText, Trash2, Upload, FileSpreadsheet, AlertCircle, CheckCircle, Mic, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VoiceInput from '@/components/VoiceInput';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';
import EditableImportReview from '@/components/EditableImportReview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayersPage({ user, onLogout, permissions }) {
  const { canEdit, canView, isReadOnly } = usePermissions(permissions);
  const navigate = useNavigate();
  const isCoachRole = user.role === 'head_coach' || user.role === 'assistant_coach';
  const [showImportReview, setShowImportReview] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importSourceHeaders, setImportSourceHeaders] = useState([]);
  const [importRawRows, setImportRawRows] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [clearRoster, setClearRoster] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [newPlayer, setNewPlayer] = useState({
    team_id: user.team_id,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    jersey_number: '',
    position: '',
    height: '',
    weight: '',
    year: '',
    dietary_restrictions: [],
    schools_of_choice: [],
    parent_first_name: '',
    parent_last_name: '',
    parent_phone: '',
    parent_email: ''
  });

  useEffect(() => {
    loadPlayers();
    // Check URL params to auto-open add dialog
    if (searchParams.get('action') === 'add') {
      setShowAddDialog(true);
      // Clear the param after opening
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players?team_id=${user.team_id}`);
      setPlayers(response.data);
    } catch (error) {
      toast.error('Failed to load players');
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      // Clean up the player data - remove empty strings for numeric fields
      const cleanedPlayer = {
        ...newPlayer,
        jersey_number: newPlayer.jersey_number === '' || isNaN(newPlayer.jersey_number) ? null : parseInt(newPlayer.jersey_number)
      };
      await axios.post(`${API}/players`, cleanedPlayer);
      toast.success('Player added successfully!');
      setShowAddDialog(false);
      loadPlayers();
      setNewPlayer({
        team_id: user.team_id,
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        jersey_number: '',
        position: '',
        height: '',
        weight: '',
        year: '',
        dietary_restrictions: [],
        schools_of_choice: [],
        parent_first_name: '',
        parent_last_name: '',
        parent_phone: '',
        parent_email: ''
      });
    } catch (error) {
      toast.error('Failed to add player');
    }
  };

  const handleRosterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel (.xlsx) file');
      return;
    }

    // Confirm if clearing roster
    if (clearRoster && !window.confirm('This will DELETE all existing players before importing. Are you sure?')) {
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Step 1: dry-run to preview parsed rows
      const previewResp = await axios.post(
        `${API}/players/roster/upload?team_id=${user.team_id}&update_existing=${updateExisting}&clear_roster=${clearRoster}&dry_run=true`,
        formData
      );

      const parsedRows = previewResp.data.rows || [];
      if (parsedRows.length === 0) {
        toast.warning('No rows parsed from the file. Check that your columns match the expected fields.');
        setUploadResult({ success: false, error: 'No rows parsed' });
      } else {
        setImportPreviewRows(parsedRows);
        setImportSourceHeaders(previewResp.data.source_headers || []);
        setImportRawRows(previewResp.data.raw_rows || []);
        setShowImportReview(true);
        setShowUploadDialog(false);  // Close the upload dialog to show the review
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload roster');
      setUploadResult({ success: false, error: error.response?.data?.detail || 'Upload failed' });
    }

    setIsUploading(false);
    // Reset file input
    e.target.value = '';
  };

  const confirmRosterImport = async (rows) => {
    setIsImporting(true);
    try {
      const resp = await axios.post(`${API}/players/roster/import`, {
        team_id: user.team_id,
        rows,
        update_existing: updateExisting,
        clear_roster: clearRoster,
      });
      toast.success(resp.data.message);
      setUploadResult(resp.data);
      setShowImportReview(false);
      setImportPreviewRows([]);
      loadPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import roster');
    } finally {
      setIsImporting(false);
    }
  };

  const handleExportRosterXlsx = async () => {
    try {
      const response = await axios.get(`${API}/players/roster/export.xlsx?team_id=${user.team_id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'roster.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export roster');
    }
  };

  const downloadTemplate = async (format = 'csv') => {
    try {
      const response = await axios.get(`${API}/players/roster/template?format=${format}`);
      
      if (format === 'excel' && response.data.encoding === 'base64') {
        // Handle Excel file (base64 encoded)
        const binaryString = atob(response.data.template);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Handle CSV file
        const blob = new Blob([response.data.template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      toast.success(`${format.toUpperCase()} template downloaded!`);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();
    try {
      // Clean up the player data - remove empty strings for numeric fields
      const cleanedPlayer = {
        ...selectedPlayer,
        jersey_number: selectedPlayer.jersey_number === '' || isNaN(selectedPlayer.jersey_number) ? null : parseInt(selectedPlayer.jersey_number)
      };
      await axios.put(`${API}/players/${selectedPlayer.id}`, cleanedPlayer);
      
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        await axios.post(`${API}/players/${selectedPlayer.id}/upload-photo`, formData);
      }
      
      toast.success('Player updated successfully!');
      setShowEditDialog(false);
      setSelectedPlayer(null);
      setPhotoFile(null);
      loadPlayers();
    } catch (error) {
      toast.error('Failed to update player');
    }
  };

  const handleExportRoster = async (format) => {
    try {
      const response = await axios.get(`${API}/players/roster/export?team_id=${user.team_id}&format=${format}`);
      
      if (format === 'csv') {
        const blob = new Blob([response.data.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'roster.csv';
        a.click();
        toast.success('Roster exported as CSV');
      } else {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'roster.json';
        a.click();
        toast.success('Roster exported as JSON');
      }
    } catch (error) {
      toast.error('Failed to export roster');
    }
  };

  const handleExportRosterDevPdf = async () => {
    try {
      const response = await axios.get(`${API}/player-intel/teams/${user.team_id}/roster-report.pdf`, {
        params: { user_id: user.id },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'roster_development_report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Roster development report downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export roster development report');
    }
  };

  const handleDeletePlayer = async (player) => {
    if (!window.confirm(`Are you sure you want to delete ${player.first_name} ${player.last_name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/players/${player.id}`);
      toast.success(`${player.first_name} ${player.last_name} has been removed from the roster`);
      loadPlayers();
    } catch (error) {
      toast.error('Failed to delete player');
    }
  };

  const openEditDialog = (player) => {
    setSelectedPlayer({...player});
    setShowEditDialog(true);
  };

  const openProfileDialog = (player) => {
    setSelectedPlayer(player);
    setShowProfileDialog(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="players-page">
        {!canView('players') ? (
          <NoAccessScreen area="Players" />
        ) : (
        <>
        {isReadOnly('players') && <ReadOnlyBanner area="Players" />}
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Player Roster
            </h1>
            <p className="text-muted-foreground mt-2">Manage your team players and their information</p>
          </div>
          <div className="flex flex-col gap-2">
            {user.role === 'head_coach' && canEdit('players') && (
              <>
                {/* Upload Roster Dialog */}
                <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="upload-roster-btn">
                      <Upload className="h-4 w-4 mr-2" />
                      UPLOAD ROSTER
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                        <FileSpreadsheet className="h-6 w-6" />
                        Import Players from CSV or Excel
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="bg-muted/50 p-4 rounded-lg border">
                        <h4 className="font-semibold mb-2">Instructions:</h4>
                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                          <li>Download the CSV or Excel template below</li>
                          <li>Fill in player information (First Name & Last Name required)</li>
                          <li>Save the file and upload it here</li>
                        </ol>
                      </div>

                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => downloadTemplate('csv')}
                        data-testid="download-csv-template-btn"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download CSV Template
                      </Button>

                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => downloadTemplate('excel')}
                        data-testid="download-excel-template-btn"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Download Excel Template
                      </Button>

                      {/* Upload Options */}
                      <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                        <Label className="text-sm font-medium">Upload Options</Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="update-existing"
                            checked={updateExisting}
                            onChange={(e) => setUpdateExisting(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="update-existing" className="text-sm">
                            Update existing players (instead of skipping duplicates)
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="clear-roster"
                            checked={clearRoster}
                            onChange={(e) => setClearRoster(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="clear-roster" className="text-sm text-destructive">
                            Clear entire roster before import (⚠️ deletes all players)
                          </label>
                        </div>
                      </div>

                      <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={handleRosterUpload}
                          className="hidden"
                          id="roster-upload"
                          disabled={isUploading}
                          data-testid="roster-file-input"
                        />
                        <label 
                          htmlFor="roster-upload" 
                          className={`cursor-pointer flex flex-col items-center gap-2 ${isUploading ? 'opacity-50' : ''}`}
                        >
                          {isUploading ? (
                            <>
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                              <span className="text-sm text-muted-foreground">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-8 w-8 text-muted-foreground" />
                              <span className="text-sm font-medium">Click to upload CSV or Excel file</span>
                              <span className="text-xs text-muted-foreground">Supports .csv, .xlsx, .xls</span>
                            </>
                          )}
                        </label>
                      </div>

                      {/* Upload Results */}
                      {uploadResult && (
                        <div className={`p-4 rounded-lg border ${uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-start gap-2">
                            {uploadResult.success ? (
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1">
                              {uploadResult.success ? (
                                <>
                                  <p className="font-medium text-green-800">{uploadResult.message}</p>
                                  <div className="text-sm text-green-700 mt-1">
                                    <span className="mr-4">Imported: {uploadResult.imported}</span>
                                    {uploadResult.skipped > 0 && <span>Skipped: {uploadResult.skipped}</span>}
                                  </div>
                                  {uploadResult.errors?.length > 0 && (
                                    <div className="mt-2 text-xs text-amber-700">
                                      <p className="font-medium">Warnings:</p>
                                      {uploadResult.errors.slice(0, 5).map((err, i) => (
                                        <p key={i}>{err}</p>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-red-800">{uploadResult.error || 'Upload failed'}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <Button 
                        variant="ghost" 
                        className="w-full" 
                        onClick={() => {
                          setShowUploadDialog(false);
                          setUploadResult(null);
                        }}
                      >
                        Close
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Add Player Dialog */}
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-player-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      ADD PLAYER
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">Add New Player</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPlayer} className="space-y-4" data-testid="add-player-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name *</Label>
                        <div className="flex gap-2">
                          <Input
                            data-testid="player-first-name"
                            value={newPlayer.first_name}
                            onChange={(e) => setNewPlayer({...newPlayer, first_name: e.target.value})}
                            required
                            className="flex-1"
                          />
                          <VoiceInput
                            onTranscript={(text) => setNewPlayer({...newPlayer, first_name: text})}
                            placeholder="Speak first name"
                            size="sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Last Name *</Label>
                        <div className="flex gap-2">
                          <Input
                            data-testid="player-last-name"
                            value={newPlayer.last_name}
                            onChange={(e) => setNewPlayer({...newPlayer, last_name: e.target.value})}
                            required
                            className="flex-1"
                          />
                          <VoiceInput
                            onTranscript={(text) => setNewPlayer({...newPlayer, last_name: text})}
                            placeholder="Speak last name"
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          data-testid="player-email"
                          value={newPlayer.email}
                          onChange={(e) => setNewPlayer({...newPlayer, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          type="tel"
                          data-testid="player-phone"
                          value={newPlayer.phone}
                          onChange={(e) => setNewPlayer({...newPlayer, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Jersey Number</Label>
                        <Input
                          type="number"
                          data-testid="player-jersey"
                          value={newPlayer.jersey_number || ''}
                          onChange={(e) => setNewPlayer({...newPlayer, jersey_number: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Position</Label>
                        <Input
                          data-testid="player-position"
                          value={newPlayer.position}
                          onChange={(e) => setNewPlayer({...newPlayer, position: e.target.value})}
                          placeholder="Guard, Forward, Center"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Height</Label>
                        <Input
                          data-testid="player-height"
                          value={newPlayer.height}
                          onChange={(e) => setNewPlayer({...newPlayer, height: e.target.value})}
                          placeholder="6'2&quot;"
                        />
                      </div>
                      <div>
                        <Label>Weight</Label>
                        <Input
                          data-testid="player-weight"
                          value={newPlayer.weight}
                          onChange={(e) => setNewPlayer({...newPlayer, weight: e.target.value})}
                          placeholder="180 lbs"
                        />
                      </div>
                      <div>
                        <Label>Year</Label>
                        <Input
                          data-testid="player-year"
                          value={newPlayer.year}
                          onChange={(e) => setNewPlayer({...newPlayer, year: e.target.value})}
                          placeholder="Freshman"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Schools of Choice (comma-separated)</Label>
                      <Input
                        data-testid="player-schools"
                        placeholder="Duke, UNC, Kentucky"
                        onChange={(e) => setNewPlayer({...newPlayer, schools_of_choice: e.target.value.split(',').map(s => s.trim())})}
                      />
                    </div>
                    
                    {/* Parent/Guardian Information */}
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-heading text-lg uppercase mb-4 text-primary">Parent/Guardian Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Parent First Name</Label>
                          <Input
                            data-testid="parent-first-name"
                            value={newPlayer.parent_first_name}
                            onChange={(e) => setNewPlayer({...newPlayer, parent_first_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Parent Last Name</Label>
                          <Input
                            data-testid="parent-last-name"
                            value={newPlayer.parent_last_name}
                            onChange={(e) => setNewPlayer({...newPlayer, parent_last_name: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <Label>Parent Phone</Label>
                          <Input
                            type="tel"
                            data-testid="parent-phone"
                            value={newPlayer.parent_phone}
                            onChange={(e) => setNewPlayer({...newPlayer, parent_phone: e.target.value})}
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        <div>
                          <Label>Parent Email</Label>
                          <Input
                            type="email"
                            data-testid="parent-email"
                            value={newPlayer.parent_email}
                            onChange={(e) => setNewPlayer({...newPlayer, parent_email: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <Button type="submit" className="w-full" data-testid="submit-player-btn">
                      ADD PLAYER
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleExportRoster('csv')} data-testid="export-csv-btn">
                <Download className="h-4 w-4 mr-2" />
                EXPORT CSV
              </Button>
              <Button variant="outline" onClick={handleExportRosterXlsx} data-testid="export-xlsx-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                EXPORT EXCEL
              </Button>
              <Button variant="outline" onClick={() => handleExportRoster('json')} data-testid="export-json-btn">
                <FileText className="h-4 w-4 mr-2" />
                EXPORT JSON
              </Button>
              {isCoachRole && (
                <Button variant="outline" onClick={handleExportRosterDevPdf} data-testid="export-roster-dev-pdf-btn">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  EXPORT DEV REPORT
                </Button>
              )}
            </div>
          </div>
        </div>

        {players.length === 0 ? (
          <Card className="border-2">
            <CardContent className="p-12 text-center">
              <User className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
              <h3 className="font-heading text-2xl mt-4">No Players Yet</h3>
              <p className="text-muted-foreground mt-2">Add your first player to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {players.map((player) => (
              <Card key={player.id} className="border-2 hover:border-primary/50 transition-all hover:-translate-y-1" data-testid={`player-card-${player.id}`}>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16 border-2 border-primary">
                      {player.photo_url ? (
                        <AvatarImage src={player.photo_url} />
                      ) : (
                        <AvatarFallback className="bg-primary text-primary-foreground font-bold text-xl">
                          {player.jersey_number || '?'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1">
                      <CardTitle className="font-heading text-xl uppercase">
                        {player.first_name} {player.last_name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">#{player.jersey_number} - {player.position || 'Position TBD'}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold">Height</p>
                      <p className="font-medium">{player.height || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold">Weight</p>
                      <p className="font-medium">{player.weight || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold">Year</p>
                      <p className="font-medium">{player.year || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold">Contact</p>
                      <p className="font-medium truncate text-xs">{player.email || player.phone || 'N/A'}</p>
                    </div>
                  </div>
                  {player.schools_of_choice && player.schools_of_choice.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold mb-2">Schools</p>
                      <div className="flex flex-wrap gap-1">
                        {player.schools_of_choice.map((school, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{school}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Parent Info Section */}
                  {(player.parent_first_name || player.parent_phone) && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-muted-foreground uppercase font-semibold mb-2">Parent/Guardian</p>
                      <div className="text-sm">
                        {player.parent_first_name && (
                          <p className="font-medium">{player.parent_first_name} {player.parent_last_name}</p>
                        )}
                        {player.parent_phone && (
                          <p className="text-xs text-muted-foreground">{player.parent_phone}</p>
                        )}
                        {player.parent_email && (
                          <p className="text-xs text-muted-foreground truncate">{player.parent_email}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1" 
                      onClick={() => openProfileDialog(player)}
                      data-testid={`view-player-${player.id}`}
                    >
                      VIEW
                    </Button>
                    {isCoachRole && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigate(`/players/${player.id}/development`)}
                        title="Development Profile"
                        data-testid={`dev-profile-player-${player.id}`}
                      >
                        <TrendingUp className="h-4 w-4" />
                      </Button>
                    )}
                    {user.role !== 'player' && canEdit('players') && (
                      <>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => openEditDialog(player)}
                          data-testid={`edit-player-${player.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleDeletePlayer(player)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600"
                          data-testid={`delete-player-${player.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        {selectedPlayer && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">Edit Player</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditPlayer} className="space-y-4" data-testid="edit-player-form">
                <div>
                  <Label>Player Photo</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    data-testid="player-photo-upload"
                    onChange={(e) => setPhotoFile(e.target.files[0])}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={selectedPlayer.first_name || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, first_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={selectedPlayer.last_name || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, last_name: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={selectedPlayer.email || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={selectedPlayer.phone || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Jersey Number</Label>
                    <Input
                      type="number"
                      value={selectedPlayer.jersey_number || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, jersey_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Input
                      value={selectedPlayer.position || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, position: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Height</Label>
                    <Input
                      value={selectedPlayer.height || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, height: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Weight</Label>
                    <Input
                      value={selectedPlayer.weight || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, weight: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Input
                      value={selectedPlayer.year || ''}
                      onChange={(e) => setSelectedPlayer({...selectedPlayer, year: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Schools of Choice (comma-separated)</Label>
                  <Input
                    value={(selectedPlayer.schools_of_choice || []).join(', ')}
                    onChange={(e) => setSelectedPlayer({...selectedPlayer, schools_of_choice: e.target.value.split(',').map(s => s.trim())})}
                  />
                </div>
                
                {/* Parent/Guardian Information */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-heading text-lg uppercase mb-4 text-primary">Parent/Guardian Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Parent First Name</Label>
                      <Input
                        value={selectedPlayer.parent_first_name || ''}
                        onChange={(e) => setSelectedPlayer({...selectedPlayer, parent_first_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Parent Last Name</Label>
                      <Input
                        value={selectedPlayer.parent_last_name || ''}
                        onChange={(e) => setSelectedPlayer({...selectedPlayer, parent_last_name: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Parent Phone</Label>
                      <Input
                        type="tel"
                        value={selectedPlayer.parent_phone || ''}
                        onChange={(e) => setSelectedPlayer({...selectedPlayer, parent_phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Parent Email</Label>
                      <Input
                        type="email"
                        value={selectedPlayer.parent_email || ''}
                        onChange={(e) => setSelectedPlayer({...selectedPlayer, parent_email: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full">
                  UPDATE PLAYER
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Profile Dialog */}
        {selectedPlayer && (
          <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-3xl uppercase">Player Profile</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="flex items-center gap-6 p-6 bg-muted/50 rounded-lg">
                  <Avatar className="h-32 w-32 border-4 border-primary">
                    {selectedPlayer.photo_url ? (
                      <AvatarImage src={selectedPlayer.photo_url} />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground font-bold text-4xl">
                        {selectedPlayer.jersey_number || '?'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1">
                    <h2 className="font-heading text-4xl font-black uppercase">
                      {selectedPlayer.first_name} {selectedPlayer.last_name}
                    </h2>
                    <p className="text-xl text-muted-foreground mt-2">
                      #{selectedPlayer.jersey_number} - {selectedPlayer.position}
                    </p>
                    {selectedPlayer.email && (
                      <p className="flex items-center gap-2 mt-2 text-sm">
                        <Mail className="h-4 w-4" />
                        {selectedPlayer.email}
                      </p>
                    )}
                  </div>
                </div>

                <Tabs defaultValue="info" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="info">Information</TabsTrigger>
                    <TabsTrigger value="parent">Parent</TabsTrigger>
                    <TabsTrigger value="schools">Schools</TabsTrigger>
                    <TabsTrigger value="stats">Statistics</TabsTrigger>
                  </TabsList>
                  <TabsContent value="info" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground uppercase font-semibold">Phone</p>
                        <p className="text-lg font-medium mt-1">{selectedPlayer.phone || 'Not provided'}</p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground uppercase font-semibold">Email</p>
                        <p className="text-lg font-medium mt-1">{selectedPlayer.email || 'Not provided'}</p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground uppercase font-semibold">Height</p>
                        <p className="text-lg font-medium mt-1">{selectedPlayer.height || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground uppercase font-semibold">Weight</p>
                        <p className="text-lg font-medium mt-1">{selectedPlayer.weight || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground uppercase font-semibold">Year</p>
                        <p className="text-lg font-medium mt-1">{selectedPlayer.year || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground uppercase font-semibold">Jersey Number</p>
                        <p className="text-lg font-medium mt-1">#{selectedPlayer.jersey_number}</p>
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="parent" className="space-y-4 mt-4">
                    <div className="p-6 bg-muted/30 rounded-lg">
                      <h3 className="font-heading text-xl uppercase mb-4">Parent/Guardian Information</h3>
                      {selectedPlayer.parent_first_name || selectedPlayer.parent_phone ? (
                        <div className="grid grid-cols-2 gap-6">
                          <div className="p-4 bg-background rounded-lg">
                            <p className="text-sm text-muted-foreground uppercase font-semibold">Name</p>
                            <p className="text-lg font-medium mt-1">
                              {selectedPlayer.parent_first_name} {selectedPlayer.parent_last_name || ''}
                            </p>
                          </div>
                          <div className="p-4 bg-background rounded-lg">
                            <p className="text-sm text-muted-foreground uppercase font-semibold">Phone</p>
                            <p className="text-lg font-medium mt-1">{selectedPlayer.parent_phone || 'Not provided'}</p>
                          </div>
                          <div className="p-4 bg-background rounded-lg col-span-2">
                            <p className="text-sm text-muted-foreground uppercase font-semibold">Email</p>
                            <p className="text-lg font-medium mt-1">{selectedPlayer.parent_email || 'Not provided'}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No parent information provided</p>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="schools" className="space-y-4 mt-4">
                    <div className="p-6 bg-muted/30 rounded-lg">
                      <h3 className="font-heading text-xl uppercase mb-4">Schools of Choice</h3>
                      {selectedPlayer.schools_of_choice && selectedPlayer.schools_of_choice.length > 0 ? (
                        <div className="space-y-2">
                          {selectedPlayer.schools_of_choice.map((school, idx) => (
                            <div key={idx} className="p-3 bg-background rounded-md border-l-4 border-primary">
                              <p className="font-semibold">{school}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No schools listed</p>
                      )}
                    </div>
                  </TabsContent>
                  <TabsContent value="stats" className="space-y-4 mt-4">
                    <div className="p-6 bg-muted/30 rounded-lg text-center">
                      <p className="text-muted-foreground">Statistics will be displayed here once games are recorded</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </DialogContent>
          </Dialog>
        )}
        </>
        )}

        <EditableImportReview
          open={showImportReview}
          onClose={() => setShowImportReview(false)}
          title="Review Roster Import"
          columns={[
            { key: 'first_name', label: 'First Name' },
            { key: 'last_name', label: 'Last Name' },
            { key: 'jersey_number', label: '#' },
            { key: 'position', label: 'Position' },
            { key: 'year', label: 'Year' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'height', label: 'Height' },
            { key: 'weight', label: 'Weight' },
            { key: 'parent_first_name', label: 'Parent First' },
            { key: 'parent_last_name', label: 'Parent Last' },
            { key: 'parent_phone', label: 'Parent Phone' },
            { key: 'parent_email', label: 'Parent Email' },
          ]}
          initialRows={importPreviewRows}
          sourceHeaders={importSourceHeaders}
          rawRows={importRawRows}
          onConfirm={confirmRosterImport}
          onSaveMappings={async (mappings) => {
            await axios.post(`${API}/teams/${user.team_id}/import-mappings`, { target: 'players', mappings });
            toast.success('Mapping remembered for future uploads');
          }}
          isImporting={isImporting}
        />
      </div>
    </Layout>
  );
}
